// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import { BeatLoader } from 'react-spinners';
import { type ShortFileProp } from '@/types/s3-file';
import React, { useEffect, useRef, useState } from 'react';
import { FileIcon, RocketIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { File as S3File } from '@/models';
import { UserPlan } from '@/models';
import { useAuth } from '@/context/auth-context/use-auth.ts';
import { API_BASE } from '@/config.ts';
import {
  getPresignedUrlsWithFolder,
  handleUpload,
  MAX_FILE_SIZE_S3_ENDPOINT,
  validateFiles,
} from '@/lib/files/file-upload-helpers.ts';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

type UploadFilesFormProps = {
  folderName: string;
  onUploadSuccess: () => void;
  onFilesSaved: (files: S3File[]) => void;
  showUpgrade?: boolean;
};

export function UploadFilesForm({
  folderName,
  onUploadSuccess,
  onFilesSaved,
  showUpgrade = true,
}: UploadFilesFormProps) {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const { user } = useAuth();
  const [plan, setPlan] = useState<UserPlan | null>(null);
  const [planLoading, setPlanLoading] = useState(true);

  const navigate = useNavigate();
  let maxFileSize = 0;

  useEffect(() => {
    if (!user) {
      setPlanLoading(false);
      return;
    }

    fetch(`${API_BASE}/api/users/${user.id}`)
      .then((res) => res.json())
      .then((data) => {
        setPlan(data.plan);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setPlanLoading(false);
      });

    switch (plan) {
      case UserPlan.FREE:
        maxFileSize = 5;
        break;
      case UserPlan.BASIC as string:
        maxFileSize = 10;
        break;
      case UserPlan.OPTIMIM as string:
        maxFileSize = 50;
        break;
      case UserPlan.EXTENDED as string:
        maxFileSize = MAX_FILE_SIZE_S3_ENDPOINT;
        break;
      default:
        maxFileSize = 0;
        break;
    }
  }, [user]);

  switch (plan) {
    case UserPlan.FREE:
      maxFileSize = 5;
      break;
    case UserPlan.BASIC as string:
      maxFileSize = 10;
      break;
    case UserPlan.OPTIMIM as string:
      maxFileSize = 50;
      break;
    case UserPlan.EXTENDED as string:
      maxFileSize = MAX_FILE_SIZE_S3_ENDPOINT;
      break;
    default:
      maxFileSize = 0;
      break;
  }

  const uploadToServer = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const files = fileInputRef.current?.files
      ? Object.values(fileInputRef.current.files)
      : [];

    if (files.length === 0) return;

    const filesInfo: ShortFileProp[] = files.map((file) => ({
      originalFileName: file.name,
      fileSize: file.size,
    }));

    setIsLoading(true);

    try {
      const filesValidationResult = validateFiles(filesInfo, maxFileSize);
      if (!filesValidationResult.success) {
        toast.error(filesValidationResult.message);
      }

      const presignedUrls = await getPresignedUrlsWithFolder(
        filesInfo,
        folderName
      );

      const progressArray = new Array(files.length).fill(0);
      setProgress(0);

      const onProgress = (fileIndex: number, progressEvent: ProgressEvent) => {
        const percent = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        updateOverallProgress(fileIndex, percent);
      };

      const updateOverallProgress = (
        fileIndex: number,
        fileProgress: number
      ) => {
        const updatedProgress = progressArray.map((progress, index) =>
          index === fileIndex ? fileProgress : progress
        );
        setProgress(updatedProgress.reduce((a, b) => a + b) / files.length);
      };

      const savedFilesRaw = await handleUpload({
        files,
        presignedUrls,
        onProgress: () => {},
        onUploadSuccess: () => {},
      });

      onFilesSaved(savedFilesRaw);
    } catch (error) {
      console.error('Upload error:', error);
      // TODO: translate
      toast.error('Failed to upload files');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e: any) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      uploadToServer(e);
    }
  };

  return (
    <form
      className="flex flex-col items-center justify-center gap-3"
      onSubmit={uploadToServer}
    >
      {showUpgrade && (
        <>
          <p className="text-md">{`${t('UploadFilesForm.fileSizeLimit')} ${maxFileSize} MB`}</p>
          <button className="text-sm text-blue-500"
                  onClick={() => navigate('/channels/@me/boost?msg=Для%20загрузки%20больших%20файлов%20требуется%20обновление%20подписки')}>
            <RocketIcon className="inline-block size-4" />{' '}
            {t('UploadFilesForm.upgradePlan')}
          </button>
        </>
      )}
      {isLoading ? (
        <div className="my-4 flex items-center justify-center">
          <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700">
            <BeatLoader />
            <div
              className="h-2.5 rounded-full bg-blue-600 dark:bg-blue-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {selectedFile && (
            <div className="mt-4 flex flex-col items-center justify-center">
              {selectedFile.type.startsWith('image/') ? (
                <img
                  src={URL.createObjectURL(selectedFile)}
                  alt={selectedFile.name}
                  className="size-16 rounded-full border object-cover"
                />
              ) : (
                // File icon for non-image files
                <div className="flex size-16 items-center justify-center rounded-full bg-gray-200">
                  <span className="text-gray-600">
                    {selectedFile.name &&
                      selectedFile.name?.split('.')?.pop()?.toUpperCase()}
                  </span>
                </div>
              )}
              <p className="mt-2 text-xs">{selectedFile.name}</p>
            </div>
          )}
          <label
            htmlFor="file"
            className="flex cursor-pointer flex-col items-center justify-center rounded-md border p-2 py-5 transition duration-150 ease-in-out hover:bg-gray-100"
          >
            <FileIcon className="size-8 text-gray-600" />
            <p className="mt-1 text-sm text-gray-600">
              {t('UploadFilesForm.uploadFiles')}
            </p>
            <input
              id="file"
              type="file"
              multiple
              className="hidden"
              required
              ref={fileInputRef}
              onChange={(e) => {
                handleFileChange(e);
              }}
            />
          </label>
        </div>
      )}
    </form>
  );
}
